import { FixedTimeRange, Timestamp } from "@/types/date"
import { SimpleHttpError } from "@/types/http"
import {
  ArticleChunk,
  BusinessType,
  ClosedPlace,
  CrawledUrlSource,
  FeedbackType,
  ImportedListing,
  MESSAGE_COUNT_FILTER_MAX,
  Organization,
  OrganizationAction,
  OrganizationList,
  OrganizationProperties,
  OrgAuditLog,
  OrgCrawledUrl,
  PlaceResolveResult,
  PlaceSuggestionBenchmark,
  PlaceSuggestionBestOf,
  PlaceSuggestionDiagnosis,
  PlaceSuggestionDmoKnob,
  PlaceSuggestionKnobsResponse,
  PlaceSuggestionPreview,
  PlaceSuggestionRunBody,
  SitePalette,
} from "@/types/organization"
import {
  PerOrganizationPermission,
  UpdateUserProfileResults,
  UserProfileUpdates,
} from "@/types/user"

import { apiBaseUrl } from "@/lib/axios-utils"
import { BaseApi } from "@/lib/base-api"
import { FeedbackData } from "@/lib/message-api"
import { OrgInspiration } from "@/components/organization/embed-snippets"
import {
  ExportHistory,
  ExportRecord,
} from "@/components/organization/export-history-dialog"
import { FeaturedOnlyOption } from "@/components/organization/featured-only-picker"
import {
  ImportHistory,
  ImportRecord,
} from "@/components/organization/import-history-dialog"
import {
  ChatFilterSettings,
  ChatInsightsTrendsEntry,
  ContentGapAnalysis,
  CreatePagesResult,
  OrgMessageData,
  OrgMessagesSummary,
  OrgMessageType,
  PageReworkResult,
  PeerComparison,
  SimilarGeoThemes,
} from "@/components/organization/message-table"
import { OrgPlaceData } from "@/components/organization/place-table"
import { PlaceTypePickerOption } from "@/components/organization/place-type-picker"
import { OrgMember } from "@/components/organization/team-roles"
import { EntityPickerOption } from "@/components/organization/user-type-picker"
import { PagingMeta } from "./paging"

export type OrgMetricsStats = {
  ai_exposed_users: number
  ai_activated_users: number
  ai_engaged_users: number
  total_chat_messages: number
  clicked_ctas: number
  ai_activation_rate: number
  ai_engagement_rate: number | null
}

export type OrgMetricsCohorts = {
  site_volume: string | null
  geo_type: string | null
  pricing_tier: string | null
  adoption_level: string | null
}

export type OrgMetricsWindow = {
  stats: OrgMetricsStats
  cohorts: OrgMetricsCohorts
}

export type OrgMetricsPeriodKind = "rolling" | "month" | "quarter"

// a reporting period the metrics snapshot was built for. calendar periods are always closed,
// and start_date/end_date are both inclusive for display.
export type OrgMetricsPeriod = {
  key: string
  kind: OrgMetricsPeriodKind
  label: string
  start_date: string
  end_date: string
}

export type OrgMetricsEntry = {
  organization_name: string
  windows: Record<string, OrgMetricsWindow>
}

export type OrgMetricsBenchmarkEntry = {
  peer_count: number
  medians: Partial<OrgMetricsStats>
}

export type OrgMetricsBenchmarkGroup = {
  all: OrgMetricsBenchmarkEntry
  site_volume: Record<string, OrgMetricsBenchmarkEntry>
  geo_type: Record<string, OrgMetricsBenchmarkEntry>
  pricing_tier: Record<string, OrgMetricsBenchmarkEntry>
  adoption_level: Record<string, OrgMetricsBenchmarkEntry>
}

export type OrgMetrics = {
  generated_at: string
  periods?: OrgMetricsPeriod[]
  orgs: Record<string, OrgMetricsEntry>
  benchmarks: Record<string, OrgMetricsBenchmarkGroup>
}

export type OrgProfileData = {
  generated_at: string
  cohorts_generated_at?: string
  organization_name: string
  periods?: OrgMetricsPeriod[]
  windows: Record<string, OrgMetricsWindow>
  benchmarks: Record<string, OrgMetricsBenchmarkGroup>
}

export type HubspotOrgData = {
  tier: string | null
  acv: string | null
  csm_name: string | null
  csm_image: string | null
  deal_owner: string | null
  company_name: string | null
  hubspot_url: string | null
  last_meeting_days: number | null
  last_qbr_days: number | null
  health: number | null
  impl_level: string | null
}

export type HubspotData = Record<string, HubspotOrgData | null>

// Keep in sync with Cs::Skills::SKILLS keys in backend.
export type CsSkillId =
  | "customer_health"
  | "adoption_path"
  | "benchmark_narrative"
  | "meeting_prep_brief"
  | "key_fact_synthesizer"
  | "config_assessment"
  | "usage_report"

export type LatestSkillResponse =
  | {
      status: "done"
      session_id: number
      content: string
      generated_at: string
    }
  | { status: "running"; session_id: number }
  | Record<string, never>

export class OrganizationApi extends BaseApi {
  async getOrganization(orgid: string): Promise<Organization | null> {
    if (!orgid || orgid === "unknown") {
      return null
    }
    try {
      return await this.get<Organization>(
        `/api/organizations/${encodeURIComponent(orgid)}`,
        // avoid redirect to signout page on 401 auth error, instead show check-settings page
        { skipAuthHandling: true }
      )
    } catch (error) {
      // allow unfamiliar organizations for Chrome extension, client will make a temporary one
      if ((error as SimpleHttpError).response?.status === 404) {
        return null
      }
      throw error
    }
  }

  getOrganizations() {
    return this.get<OrganizationList>("api/organizations")
  }

  getHubspotData() {
    return this.get<HubspotData>("api/organizations/hubspot_data")
  }

  getMetrics() {
    return this.get<OrgMetrics>("api/organizations/metrics")
  }

  getOrgProfile(orgid: string) {
    return this.get<OrgProfileData>(`/api/organizations/${orgid}/profile`)
  }

  runSkill(orgid: string, skillId: CsSkillId) {
    return this.post<{ session_id: number }>(
      `/api/organizations/${orgid}/skills/${skillId}/run`
    )
  }

  getLatestSkill(orgid: string, skillId: CsSkillId) {
    return this.get<LatestSkillResponse>(
      `/api/organizations/${orgid}/skills/${skillId}/latest`
    )
  }

  getMetricsDownloadUrl(period: string): string {
    return `${apiBaseUrl()}/api/organizations/metrics_download?period=${encodeURIComponent(period)}`
  }
  async requestAnswerIntelligence(orgid: string) {
    return await this.post<{ ok: boolean }>(
      `/api/organizations/${orgid}/request_answer_intelligence`,
      {}
    )
  }

  createOrganization(
    orgid: string,
    name: string,
    website_url: string,
    status = "pending",
    properties: OrganizationProperties = {},
    business_type: BusinessType = "destination"
  ) {
    return this.post("/api/organizations", {
      orgid,
      name,
      website_url,
      status,
      properties,
      business_type,
    })
  }
  async fetchInternalUsers(): Promise<OrgMember[]> {
    return this.get(`/api/store-admin/internal_users`)
  }

  async addInternalUser(email: string, role: string): Promise<OrgMember[]> {
    return this.post(`/api/store-admin/internal_users`, { email, role })
  }

  async updateInternalUserRole(
    userid: number,
    role: string
  ): Promise<OrgMember[]> {
    return this.put(`/api/store-admin/internal_users/${userid}`, { role })
  }

  async removeInternalUser(userid: number): Promise<OrgMember[]> {
    return this.delete(`/api/store-admin/internal_users/${userid}`)
  }

  async acceptInvitation(code: string) {
    return await this.post<{
      orgid: string
    }>(`/api/organizations/accept/${code}`)
  }
  async fetchSitemapPathHisto(orgid: string): Promise<{
    paths: Record<
      string,
      {
        count: number
        sample_url: string
        status: "included" | "excluded" | "partial"
      }
    >
    errors: string[]
  }> {
    return this.get(`/api/organizations/${orgid}/sitemap_path_histo`)
  }
  async disassociateChat(
    orgid: string,
    chatId: number
  ): Promise<{ chat_id: number; trip_id: number }> {
    return this.delete(
      `/api/organizations/${orgid}/chats/${chatId}/disassociate`
    )
  }

  async fetchChatMessages(
    orgid: string,
    chatId: number,
    page: number,
    perPage: number,
    includeInitial?: boolean
  ): Promise<{
    data: OrgMessageData[]
    meta: PagingMeta & { chat_deactivated_at?: Timestamp | null }
  }> {
    const url = new URL(
      `${apiBaseUrl()}/api/organizations/${orgid}/chat_messages`
    )
    const params = url.searchParams

    params.set("chat_id", chatId.toString())
    params.set("page", page.toString())
    params.set("per_page", perPage.toString())
    if (includeInitial) params.set("include_initial", "true")

    return this.get(url.toString())
  }

  async fetchChatInsights(orgid: string): Promise<OrgMessagesSummary> {
    return this.get(`/api/organizations/${orgid}/chat_insights`)
  }

  async fetchChatInsightsTrends(
    orgid: string
  ): Promise<{ trends: ChatInsightsTrendsEntry[] }> {
    return this.get(`/api/organizations/${orgid}/chat_insights_trends`)
  }

  async fetchChatInsightsRun(
    orgid: string,
    id: number
  ): Promise<{
    content_gaps: ContentGapAnalysis
    chat_filters?: ChatFilterSettings
  }> {
    return this.get(`/api/organizations/${orgid}/chat_insights_runs/${id}`)
  }

  async fetchSimilarGeoInsights(
    orgid: string
  ): Promise<{ geos: SimilarGeoThemes[]; comparison: PeerComparison | null }> {
    return this.get(`/api/organizations/${orgid}/similar_geo_insights`)
  }

  async runChatAnalysis(
    orgid: string,
    opts?: { full?: boolean }
  ): Promise<{ analysis_pending: boolean }> {
    return this.post(`/api/organizations/${orgid}/run_chat_analysis`, {
      full: opts?.full ?? false,
    })
  }

  async pageRework(
    orgid: string,
    params: {
      page_url: string
      page_title: string
      topics: {
        topic: string
        details: string
        coverage: string
        chat_count: number
        sample_questions: string[]
      }[]
      force?: boolean
    }
  ): Promise<PageReworkResult> {
    return this.post(`/api/organizations/${orgid}/page_rework`, params)
  }

  async createPages(
    orgid: string,
    params: {
      topic: string
      details: string
      sample_questions: string[]
      // pages the topic already matches, so a draft differentiates from them
      articles: { url: string; title: string }[]
      force?: boolean
    }
  ): Promise<CreatePagesResult> {
    return this.post(`/api/organizations/${orgid}/create_pages`, params)
  }

  async exportPages(
    orgid: string,
    body: { topics: string[]; file_format: "html" | "docx" }
  ): Promise<{ filename: string; content_base64: string }> {
    return this.post(`/api/organizations/${orgid}/export_pages`, body)
  }

  async contentPageDownload(
    orgid: string,
    params: { topic: string; index: number; file_format: "html" | "docx" }
  ): Promise<{ html?: string; content_base64?: string }> {
    return this.post(
      `/api/organizations/${orgid}/content_page_download`,
      params
    )
  }
  async downloadMessagesTrigger(
    orgid: string,
    timeRange: FixedTimeRange,
    messageType: OrgMessageType,
    chatId?: number,
    userSatFilter?: [number, number],
    messageCountFilter?: [number, number],
    intentFilter?: string,
    feedbackFilter?: FeedbackType,
    filters: {
      text?: string
      originFilter?: string
      languageFilter?: string
      channel?: "whatsapp"
    } = {}
  ): Promise<{}> {
    const url = new URL(
      `${apiBaseUrl()}/api/organizations/${orgid}/messages_download`
    )
    const params = url.searchParams

    params.set("start_date", timeRange.from.epoch_millis.toString())
    params.set("end_date", timeRange.to.epoch_millis.toString())

    if (messageType) params.set("message_type", messageType)
    if (chatId) params.set("chat_id", chatId.toString())
    if (userSatFilter) {
      const [min, max] = userSatFilter
      const expandedScores = Array.from(
        { length: max - min + 1 },
        (_, i) => min + i
      )
      params.set("csat_scores", expandedScores.join(","))
    }
    if (messageCountFilter) {
      const [min, max] = messageCountFilter
      params.set("message_count_min", min.toString())
      if (max < MESSAGE_COUNT_FILTER_MAX) {
        params.set("message_count_max", max.toString())
      }
    }
    if (intentFilter) params.set("intent", intentFilter)
    if (feedbackFilter) params.set("feedback_type", feedbackFilter)
    if (filters.text) params.set("text", filters.text)
    if (filters.originFilter) params.set("origin", filters.originFilter)
    if (filters.languageFilter) params.set("language", filters.languageFilter)
    if (filters.channel) params.set("channel", filters.channel)

    return this.get(url.toString())
  }
  downloadUsersUrl(
    orgid: string,
    timeRange: FixedTimeRange,
    userType: EntityPickerOption
  ): string {
    const url = new URL(
      `${apiBaseUrl()}/api/organizations/${orgid}/users_download`
    )
    const params = url.searchParams

    params.set("start_date", timeRange.from.epoch_millis.toString())
    params.set("end_date", timeRange.to.epoch_millis.toString())
    params.set("user_type", userType)

    return url.toString()
  }

  async fetchInspirations(
    orgid: string,
    page: number,
    perPage: number
  ): Promise<{ data: OrgInspiration[]; meta: PagingMeta }> {
    return this.get(
      `/api/organizations/${orgid}/inspirations?page=${page}&per_page=${perPage}`
    )
  }

  async fetchPlaces(
    orgid: string,
    timeRange: FixedTimeRange,
    page: number,
    perPage: number,
    placeType: PlaceTypePickerOption,
    featuredOnly: FeaturedOnlyOption,
    searchFilter: string,
    sort_by?: string,
    sort_order?: string
  ): Promise<{
    data: OrgPlaceData[]
    meta: PagingMeta & {
      mention_count: number
      featured_count: number
      handoff_count: number
      view_count: number
    }
  }> {
    const url = new URL(`${apiBaseUrl()}/api/organizations/${orgid}/places`)
    const params = url.searchParams

    params.set("start_date", timeRange.from.epoch_millis.toString())
    params.set("end_date", timeRange.to.epoch_millis.toString())
    params.set("page", page.toString())
    params.set("per_page", perPage.toString())
    params.set("place_type", placeType.toString())
    params.set("featured_only", featuredOnly.toString())

    if (sort_by) params.set("sort_by", sort_by)
    if (sort_order) params.set("sort_order", sort_order)
    if (searchFilter) params.set("filter", searchFilter)

    return this.get(url.toString())
  }

  async downloadPlacesTrigger(
    orgid: string,
    timeRange: FixedTimeRange,
    placeType: PlaceTypePickerOption,
    featuredOnly: FeaturedOnlyOption,
    searchFilter: string
  ): Promise<{}> {
    const url = new URL(
      `${apiBaseUrl()}/api/organizations/${orgid}/places_download`
    )
    const params = url.searchParams
    params.set("start_date", timeRange.from.epoch_millis.toString())
    params.set("end_date", timeRange.to.epoch_millis.toString())
    params.set("place_type", placeType.toString())
    params.set("featured_only", featuredOnly.toString())
    if (searchFilter) {
      params.set("filter", searchFilter)
    }
    return this.get(url.toString())
  }

  async fetchAuditLogs(
    orgid: string,
    page: number,
    perPage: number
  ): Promise<{ data: OrgAuditLog[]; meta: PagingMeta }> {
    const url = new URL(`${apiBaseUrl()}/api/organizations/${orgid}/audit_logs`)
    const params = url.searchParams
    params.set("page", page.toString())
    params.set("per_page", perPage.toString())

    return this.get(url.toString())
  }

  async fetchCrawledUrls(
    orgid: string,
    timeRange: FixedTimeRange | undefined,
    filter: string,
    onlyActive: boolean,
    page: number,
    perPage: number,
    kind: CrawledUrlSource
  ): Promise<{ data: OrgCrawledUrl[]; meta: PagingMeta }> {
    const url = new URL(
      `${apiBaseUrl()}/api/organizations/${orgid}/crawled_urls`
    )
    const params = url.searchParams
    if (timeRange) {
      params.set("start_date", timeRange.from.epoch_millis.toString())
      params.set("end_date", timeRange.to.epoch_millis.toString())
    }
    if (filter) {
      params.set("filter", filter)
    }
    params.set("only_active", onlyActive.toString())
    params.set("page", page.toString())
    params.set("per_page", perPage.toString())
    params.set("kind", kind)

    return this.get(url.toString())
  }

  async downloadCrawledUrlsTrigger(
    orgid: string,
    filter: string,
    onlyActive: boolean,
    kind?: string
  ): Promise<{}> {
    const url = new URL(
      `${apiBaseUrl()}/api/organizations/${orgid}/crawled_urls_download`
    )
    const params = url.searchParams
    if (filter) {
      params.set("filter", filter)
    }
    params.set("only_active", onlyActive.toString())
    if (kind) {
      params.set("kind", kind)
    }

    return this.get(url.toString())
  }

  async fetchClosedPlaces(
    orgid: string,
    articleId: number
  ): Promise<{ data: ClosedPlace[] }> {
    const url = `${apiBaseUrl()}/api/organizations/${orgid}/crawled_urls/${articleId}/closed_places`
    return this.get(url)
  }

  async fetchArticleChunks(
    orgid: string,
    articleId: number
  ): Promise<{ data: ArticleChunk[] }> {
    const url = `${apiBaseUrl()}/api/organizations/${orgid}/crawled_urls/${articleId}/chunks`
    return this.get(url)
  }

  async fetchExports(orgid: string): Promise<ExportHistory> {
    const response = await this.get<{
      exports: Array<ExportRecord>
    }>(`/api/organizations/${orgid}/exports`)

    return {
      exports: response.exports,
    }
  }

  async fetchImports(orgid: string): Promise<ImportHistory> {
    const response = await this.get<{
      imports: Array<ImportRecord>
    }>(`/api/organizations/${orgid}/imports`)

    return {
      imports: response.imports,
    }
  }

  downloadImportFileUrl(
    orgid: string,
    importId: number,
    fileType: string
  ): string {
    return `${apiBaseUrl()}/api/organizations/${orgid}/imports/${importId}/${fileType}`
  }
  async getMetabaseEmbedUrl(
    orgid: string,
    dashboardId: string,
    startDate?: string,
    endDate?: string,
    frequency?: string,
    theme?: string,
    country?: string
  ): Promise<{ embed_url: string; base_url: string }> {
    const params = new URLSearchParams()
    params.set("dashboard_id", dashboardId)
    if (startDate) params.set("start_date", startDate)
    if (endDate) params.set("end_date", endDate)
    if (frequency) params.set("time_interval", frequency)
    if (theme) params.set("theme", theme)
    if (country) params.set("country", country)

    return this.get(`/api/organizations/${orgid}/metabase_embed_url?${params}`)
  }

  uploadPlaces(orgid: string, formData: FormData): Promise<ImportedListing[]> {
    return this.post(`/api/organizations/${orgid}/places_upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  }

  importPlaces(
    orgid: string,
    formData: FormData
  ): Promise<{ job_id: number; state: string }> {
    return this.post(`/api/organizations/${orgid}/places_import`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  }

  reimportPlaces(orgid: string): Promise<{ job_id: number; state: string }> {
    return this.post(`/api/organizations/${orgid}/places_reimport`)
  }

  async uploadDocuments(orgid: string, formData: FormData): Promise<void> {
    return this.post(`/api/organizations/${orgid}/documents_upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  }

  async deactivateDocument(orgid: string, documentId: number): Promise<void> {
    return this.delete(`/api/organizations/${orgid}/documents/${documentId}`)
  }

  // expiresOn is YYYY-MM-DD in the org's time zone, or null for no expiration
  async updateDocumentExpiration(
    orgid: string,
    documentId: number,
    expiresOn: string | null
  ): Promise<void> {
    return this.patch(`/api/organizations/${orgid}/documents/${documentId}`, {
      expires_on: expiresOn ?? "",
    })
  }

  async placeResolve(
    orgid: string,
    listing: ImportedListing
  ): Promise<PlaceResolveResult> {
    return this.post<PlaceResolveResult>(
      `/api/organizations/${orgid}/place_resolve`,
      listing
    )
  }

  async fetchPlaceSuggestionKnobs(
    orgid: string
  ): Promise<PlaceSuggestionKnobsResponse> {
    return this.get(`/api/organizations/${orgid}/place_suggestions/knobs`)
  }

  // Persist this org's best-of curation policy (the semicolon override string
  // applied to vague best-of searches) plus the doctor's eval assets (freeform
  // DMO notes + saved benchmark queries). The backend validates the curation
  // string against the live facet schema and 422s on any invalid key/value.
  async savePlaceSuggestionPolicy(
    orgid: string,
    body: {
      best_of_policy: string
      property_overrides: Record<string, unknown>
      notes?: string
      benchmarks?: PlaceSuggestionBenchmark[]
    }
  ): Promise<{
    best_of: PlaceSuggestionBestOf
    dmo_knobs: PlaceSuggestionDmoKnob[]
    notes: string | null
    benchmarks: PlaceSuggestionBenchmark[]
  }> {
    return this.put(
      `/api/organizations/${orgid}/place_suggestions/policy`,
      body
    )
  }

  async fetchPlaceSuggestionPreview(
    orgid: string,
    body: PlaceSuggestionRunBody
  ): Promise<PlaceSuggestionPreview> {
    return this.post(
      `/api/organizations/${orgid}/place_suggestions/preview`,
      body
    )
  }

  // On-demand LLM "search doctor" for the current single query. Slow (one Gemini
  // call) — BaseApi transparently awaits the streamed result.
  async diagnosePlaceSuggestions(
    orgid: string,
    body: PlaceSuggestionRunBody & { notes?: string }
  ): Promise<PlaceSuggestionDiagnosis> {
    return this.post(
      `/api/organizations/${orgid}/place_suggestions/diagnose`,
      body
    )
  }

  // Batch eval: score this DMO's benchmark set in one Gemini call. The current
  // (possibly unsaved) benchmarks are sent so eval works before Save; the
  // backend falls back to the saved set when none are passed.
  async evaluatePlaceSuggestions(
    orgid: string,
    benchmarks: PlaceSuggestionBenchmark[]
  ): Promise<PlaceSuggestionDiagnosis> {
    return this.post(`/api/organizations/${orgid}/place_suggestions/evaluate`, {
      benchmarks,
    })
  }

  async downloadOrganizationsCsv(
    status: string,
    search?: string
  ): Promise<Blob> {
    const url = new URL(`${apiBaseUrl()}/api/organizations`)
    const params = url.searchParams

    params.set("download", "csv")
    params.set("status", status)
    if (search) {
      params.set("search", search)
    }

    return await this.get<Blob>(url.toString(), {
      responseType: "blob",
    })
  }

  async updateFeaturedPlace(
    orgid: string,
    entity_ref: string,
    feature_level: number
  ): Promise<{ entity_ref: string; feature_level: number }> {
    return this.put(`/api/organizations/${orgid}/places/${entity_ref}`, {
      feature_level,
    })
  }

  async removeFeaturedPlace(
    orgid: string,
    entity_ref: string
  ): Promise<{ entity_ref: string; removed: boolean }> {
    return this.delete(`/api/organizations/${orgid}/places/${entity_ref}`)
  }

  // returns per-org permissions plus the user's mindtrip engineer flag
  async getPermissionsForOrg(orgid: string): Promise<{
    permissions: PerOrganizationPermission[]
    isEngineer: boolean
  }> {
    const { permissions, is_engineer } = await this.get<{
      permissions: PerOrganizationPermission[]
      is_engineer?: boolean
    }>(`api/organizations/${encodeURIComponent(orgid)}/permissions`)
    return { permissions, isEngineer: !!is_engineer }
  }
  downloadFeedbacksUrl(
    orgid: string,
    startDate?: string,
    endDate?: string,
    statuses?: string[],
    feedbackType?: "thumbs_up" | "thumbs_down"
  ): string {
    const params = new URLSearchParams()

    if (startDate) params.append("start_date", startDate)
    if (endDate) params.append("end_date", endDate)
    if (statuses && statuses.length > 0) {
      params.append("statuses", statuses.join(","))
    }
    if (feedbackType) {
      params.append("feedback_type", feedbackType)
    }

    const queryString = params.toString()
    return `${apiBaseUrl()}/api/organizations/${orgid}/feedbacks_download?${queryString}`
  }

  async fetchCtaInsights(orgid: string, limit = 50): Promise<CtaInsight[]> {
    const url = new URL(
      `${apiBaseUrl()}/api/organizations/${orgid}/cta_insights`
    )
    url.searchParams.set("limit", limit.toString())
    return await this.get<CtaInsight[]>(url.toString())
  }

  async fetchOrganizationActions(
    orgid: string,
    page: number,
    perPage: number
  ): Promise<{ data: OrganizationAction[]; meta: PagingMeta }> {
    const url = new URL(`${apiBaseUrl()}/api/organizations/${orgid}/actions`)
    const params = url.searchParams
    params.set("page", page.toString())
    params.set("per_page", perPage.toString())

    return this.get(url.toString())
  }

  async createOrganizationAction(
    orgid: string,
    actionId: string,
    buttonLabel: string,
    userPrompt?: string,
    messageHint?: string,
    promptEditable?: boolean,
    guidePath?: string,
    placePath?: string,
    mediaLink?: string
  ): Promise<OrganizationAction> {
    return this.post(`/api/organizations/${orgid}/actions`, {
      action_id: actionId,
      button_label: buttonLabel,
      user_prompt: userPrompt,
      message_hint: messageHint,
      prompt_editable: promptEditable,
      guide_path: guidePath,
      place_path: placePath,
      media_link: mediaLink,
    })
  }

  async updateOrganizationAction(
    orgid: string,
    id: number,
    actionId: string,
    buttonLabel: string,
    userPrompt?: string,
    messageHint?: string,
    promptEditable?: boolean,
    guidePath?: string,
    placePath?: string,
    mediaLink?: string
  ): Promise<OrganizationAction> {
    return this.put(`/api/organizations/${orgid}/actions/${id}`, {
      action_id: actionId,
      button_label: buttonLabel,
      user_prompt: userPrompt,
      message_hint: messageHint,
      prompt_editable: promptEditable,
      guide_path: guidePath,
      place_path: placePath,
      media_link: mediaLink,
    })
  }

  async deleteOrganizationAction(
    orgid: string,
    id: number
  ): Promise<{ message: string }> {
    return this.delete(`/api/organizations/${orgid}/actions/${id}`)
  }

  async createCreatorUser(
    orgid: string,
    handle: string,
    firstName?: string,
    bio?: string,
    profilePhoto?: File
  ): Promise<{
    handle: string
    first_name?: string
    bio?: string
    image_url?: string
  }> {
    const formData = new FormData()
    formData.append("handle", handle)
    if (firstName) {
      formData.append("first_name", firstName)
    }
    if (bio) {
      formData.append("bio", bio)
    }
    if (profilePhoto) {
      formData.append("profile_photo", profilePhoto)
    }
    return this.post(
      `/api/organizations/${orgid}/create_creator_user`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    )
  }

  async assignCreator(
    orgid: string,
    handle: string
  ): Promise<{ handle: string; email: string }> {
    return this.put(`/api/organizations/${orgid}/creator`, { handle })
  }

  async fetchCreatorInfo(orgid: string): Promise<{
    handle: string
    email: string
  } | null> {
    try {
      return await this.get(`/api/organizations/${orgid}/creator`)
    } catch (error) {
      return null
    }
  }

  async updateCreatorProfile(
    orgid: string,
    profileData: UserProfileUpdates
  ): Promise<UpdateUserProfileResults> {
    return await this.put(`/api/organizations/${orgid}/creator/profile`, {
      user_profile: profileData,
    })
  }

  async uploadCreatorAvatar(
    orgid: string,
    formData: FormData
  ): Promise<{ url: string; error?: string }> {
    return await this.post(
      `/api/organizations/${orgid}/creator/avatar`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )
  }

  async uploadLogo(
    orgid: string,
    formData: FormData
  ): Promise<{ url: string; error?: string }> {
    return await this.post(
      `/api/organizations/${orgid}/upload_logo`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )
  }

  // synchronous rescrape of the org homepage; null means not readable
  async refreshSitePalette(
    orgid: string
  ): Promise<{ site_palette: SitePalette | null }> {
    return await this.post(
      `/api/organizations/${orgid}/refresh_site_palette`,
      {}
    )
  }

  async fetchSnippetStatus(orgid: string): Promise<SnippetStatusResponse> {
    return this.get(`/api/organizations/${orgid}/snippet_status`)
  }

  async fetchBacklinkStatus(orgid: string): Promise<BacklinkStatusResponse> {
    return this.get(`/api/organizations/${orgid}/backlink_status`)
  }

  async getSecureProperties(
    orgid: string
  ): Promise<Record<string, string> | null> {
    return this.get(`/api/organizations/${orgid}/secure_properties`)
  }

  async updateSecureProperties(
    orgid: string,
    properties: Record<string, string>
  ): Promise<{ success: boolean }> {
    return this.put(`/api/organizations/${orgid}/secure_properties`, {
      secure_properties: properties,
    })
  }

  async testSecureProperty(
    orgid: string,
    key: string,
    value?: string
  ): Promise<{ success: boolean; message: string }> {
    return this.post(`/api/organizations/${orgid}/test_secure_property`, {
      key,
      ...(value !== undefined && { value }),
    })
  }

  async provisionWhatsappTemplates(
    orgid: string
  ): Promise<{ success: boolean; message: string }> {
    return this.post(
      `/api/organizations/${orgid}/provision_whatsapp_templates`,
      {}
    )
  }

  async getWhatsappTemplateStatuses(orgid: string): Promise<{
    success: boolean
    message?: string
    templates?: Array<{
      n: number
      name: string
      sid: string
      status: string
      category: string | null
      rejection_reason: string | null
    }>
  }> {
    return this.get(`/api/organizations/${orgid}/whatsapp_template_statuses`)
  }

  async uploadListingsStaging(
    orgid: string,
    formData: FormData
  ): Promise<ListingsStagingUploadResponse> {
    return this.post(
      `/api/organizations/${orgid}/listings_staging_upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    )
  }

  async fetchListingsStaging(
    orgid: string,
    page: number,
    perPage: number
  ): Promise<ListingsStagingResponse> {
    const url = new URL(
      `${apiBaseUrl()}/api/organizations/${orgid}/listings_staging`
    )
    const params = url.searchParams
    params.set("page", page.toString())
    params.set("per_page", perPage.toString())

    return this.get(url.toString())
  }

  async createMessageFeedback(
    orgid: string,
    messageId: number,
    data: FeedbackData
  ): Promise<void> {
    return this.post(
      `/api/organizations/${orgid}/messages/${messageId}/feedback`,
      data
    )
  }
}

export interface ListingsStagingFile {
  filename: string
  s3_url: string
  uploaded_at: string
  download_url: string
}

export interface ListingsStagingUploadResponse {
  filename: string
  file_size: number
  content_type: string
  s3_url: string
  uploaded_at: string
}

export interface ListingsStagingResponse {
  data: ListingsStagingFile[]
  meta: {
    current_page: number
    per_page: number
    total_pages: number
    total_count: number
  }
}

export interface SnippetHostStatus {
  host: string
  last_event_at: Timestamp
}

export interface SnippetStatusResponse {
  hosts: SnippetHostStatus[]
}

export interface BacklinkLink {
  href: string
  text: string
  html: string
}

export interface BacklinkStatusResponse {
  found: boolean
  url: string
  links: BacklinkLink[]
  error?: string
}

export interface CtaInsight {
  cta_id: string | null
  cta_type: string | null
  prompt: string | null
  action_cue: string | null
  count: number
  source_urls: string[]
}

export interface OrgFeedbackData {
  id: number
  type: "message" | "entity"
  chat_id: number
  message_id: number
  message_text: string
  place_id: number | null
  place_name: string | null
  place_url: string | null
  thumbs_up: boolean
  status: string | null
  created_at: Timestamp
  user_comment: string | null
  mindtrip_comment: string | null
  customer_comment: string | null
}
